/**
 * CaseIngestionBanner
 *
 * Prominent banner that appears above the Case Overview card when the matter
 * is in "Case Ingestion" status. Communicates that tasks are hidden from the
 * task board until case setup is complete, and encourages the user to finish
 * ingestion to activate them.
 */
import { ArrowRight, FolderInput } from 'lucide-react';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { usePostHogCapture } from '@/hooks/usePostHogCapture';
import { useGetMatterDetailsQuery } from '@/services/api/mattersService';
import { MatterStatus } from '@/services/types/client-intake-types';

interface CaseIngestionBannerProps {
  matterId: string;
}

export const CaseIngestionBanner = ({ matterId }: CaseIngestionBannerProps) => {
  const navigate = useNavigate();
  const capture = usePostHogCapture();

  // Fetch matter to check status
  const { data: matter } = useGetMatterDetailsQuery(matterId, {
    skip: !matterId,
  });

  // Show banner only if matter is in CASE_INGESTION status
  const shouldShow = matter?.status === MatterStatus.CASE_INGESTION;

  // Handle banner CTA click with analytics
  const handleContinueSetup = useCallback(() => {
    capture('case_ingestion_banner_cta_clicked', {
      matter_id: matterId,
    });
    navigate(`/matters/${matterId}/ingestion`);
  }, [capture, matterId, navigate]);

  if (!shouldShow) {
    return null;
  }

  return (
    <div className="rounded-md border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950 overflow-hidden">
      <div className="px-3 py-3 flex items-center justify-between gap-3">
        {/* Left: Icon and text */}
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-amber-100 dark:bg-amber-900">
            <FolderInput className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100">
              Case Ingestion in Progress
            </h3>
            <p className="text-xs font-medium text-amber-700 dark:text-amber-300 mt-0.5">
              Tasks for this matter won&apos;t appear on the task board until
              case setup is complete. Finish ingestion to make them visible and
              actionable.
            </p>
          </div>
        </div>

        {/* Right: CTA button */}
        <Button
          onClick={handleContinueSetup}
          size="sm"
          className="flex-shrink-0 gap-1.5 bg-amber-600 text-white hover:bg-amber-700 dark:bg-amber-600 dark:hover:bg-amber-500"
        >
          Complete Setup
          <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};
